import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import Thunk from 'redux-thunk';
import reducers from '@app/reducer';

import { ReducerRegistry } from '../core-bundle';

// Import initial state helpers
import channel from './helper/channel';
import layout from './helper/layout';
import notification from './helper/notification';
import pending from './helper/pending';
import route from './helper/route';
import session from './helper/session';
import transfer from './helper/transfer';
import user from './helper/user';
import account from './helper/account';
import invitation from './helper/invitation';

import ui from './helper/ui';

// store enhancers for analytics events
import actionListener from './enhancer/action-listener';
import stateChangeListener from './enhancer/state-change-listener';

// Create our initial state
const initialState = {
  // Some channel specific information
  channel,

  layout,

  // This contains notifications (if needed)
  notification,

  // Everything that is pending in our app is stored here
  pending,

  // Some account specific information
  account,

  // Some invitation specific information
  invitation,

  // This contains the current route
  route,

  // Some session specific information
  session,

  // This contains the current Transfer object (up/down)
  // 1-to-1: A User can only have one active Transfer Object
  transfer,

  ui,

  billingDetails: {},

  invoices: null,

  apiGrants: [],

  // Some user specific information
  user,
};

const middlewares = [Thunk];

if (__DEV__ && __REDUX_LOGGER__) {
  const { createLogger } = require('redux-logger');
  middlewares.push(
    createLogger({
      diff: true,
      // Collapse actions that don't have errors
      collapsed: (getState, action, logEntry) => !logEntry.error,
    })
  );
}

const composeEnhancers =
  __DEV__ === true &&
  typeof window === 'object' &&
  window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
    : compose;

// Preserve initial state for not-yet-loaded reducers
function combine(reducers) {
  const reducerNames = Object.keys(reducers);
  Object.keys(initialState).forEach(function initState(item) {
    if (!reducerNames.includes(item)) {
      reducers[item] = (state = null) => state;
    }
  });
  return combineReducers(reducers);
}

// We'll create a Store that has our Reducers and an initial state,
// with added Thunk middleware to make async actions possible.
const store = createStore(
  combine({
    // Statically defined reducers
    ...reducers,
    // Lazy loaded reducers
    ...ReducerRegistry.reducers,
  }),
  initialState,
  composeEnhancers(
    applyMiddleware(...middlewares),
    actionListener,
    stateChangeListener
  )
);

// Replace the store's reducer whenever a new reducer is registered.
ReducerRegistry.setChangeListener((asyncReducers) => {
  store.replaceReducer(
    combine({
      // Statically defined reducers
      ...reducers,
      // Lazy loaded reducers
      ...asyncReducers,
    })
  );
});

export default store;
